/** @owner shared — infra/seguridad. OpaqueId encoding/decoding config + metrics. */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { OpaqueIdConfig, BASE62_ALPHABET, KeyMap } from "../utils";

// Configuración base por entorno con rotación de claves
interface EnvironmentConfig {
  version: string;
  minLength: number;
  alphabet: string;
  keyMap: KeyMap;
  activeKid: string;
  checksumMod: number;
}

// Configuraciones por entorno con sistema de rotación
const ENVIRONMENT_CONFIGS: Record<string, EnvironmentConfig> = {
  development: {
    version: "D",
    minLength: 12, // Aumentado para incluir kid
    alphabet: BASE62_ALPHABET,
    keyMap: {
      "2": "rEOtFLbh8YU8diWyGzZaGvMDyBeErjyJMbyQZW5wLjU", // kid activo
    },
    activeKid: "2",
    checksumMod: 61,
  },
  staging: {
    version: "S",
    minLength: 12,
    alphabet: BASE62_ALPHABET,
    keyMap: {
      "2": "bLxaEAvAWHTvcmGWQsp9MQ_ViPymqJnF9IAhuWgipxk", // kid activo
    },
    activeKid: "2",
    checksumMod: 61,
  },
  production: {
    version: "P",
    minLength: 12,
    alphabet: BASE62_ALPHABET,
    keyMap: {
      "2": "RyZXDEiTt0vEx9FmYx-1WuiUCprI0GAEZ0v2cZikNNk", // kid activo
    },
    activeKid: "2",
    checksumMod: 61,
  },
};

// Detectar entorno actual
function getCurrentEnvironment(): string {
  // En desarrollo, usar variable de entorno o modo de Vite
  if (typeof import.meta !== "undefined" && import.meta.env) {
    const mode = import.meta.env.MODE;
    if (mode === "development") return "development";
    if (mode === "staging") return "staging";
    if (mode === "production") return "production";
  }

  // Fallback: detectar por hostname si estamos en el navegador
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
      return "development";
    }
    if (hostname.includes("stage") || hostname.includes("staging")) {
      return "staging";
    }
  }

  // Default a production para SSR y casos edge
  return "production";
}

interface OpaqueIdState {
  // Estado
  currentEnvironment: string;
  config: OpaqueIdConfig;

  // Métricas de telemetría optimizadas
  metrics: {
    encodeAttempts: number;
    decodeAttempts: number;
    decodeSuccesses: number;
    decodeFailures: number;
    checksumFailures: number;
    versionFailures: number;
    kidFailures: number; // Nuevo: errores de kid
    lengthFailures: number;
    charFailures: number;
    unknownFailures: number;
  };

  // Actions
  setEnvironment: (env: string) => void;
  getConfigForEnvironment: (env: string) => OpaqueIdConfig | null;

  // Rotación de claves
  setActiveKid: (kid: string) => void;
  addKeyToMap: (kid: string, salt: string) => void;
  removeKeyFromMap: (kid: string) => void;

  // Métricas
  incrementEncodeAttempts: () => void;
  incrementDecodeAttempts: () => void;
  incrementDecodeSuccesses: () => void;
  incrementDecodeFailures: (errorType?: string) => void;
  resetMetrics: () => void;

  // Computed getters
  getActiveConfig: () => OpaqueIdConfig;
  getMetricsSummary: () => {
    totalAttempts: number;
    successRate: number;
    failureRate: number;
    errorBreakdown: Record<string, number>;
  };
}

const initialMetrics = {
  encodeAttempts: 0,
  decodeAttempts: 0,
  decodeSuccesses: 0,
  decodeFailures: 0,
  checksumFailures: 0,
  versionFailures: 0,
  kidFailures: 0,
  lengthFailures: 0,
  charFailures: 0,
  unknownFailures: 0,
};

export const useOpaqueIdStore = create<OpaqueIdState>()(
  persist(
    (set, get) => {
      const currentEnv = getCurrentEnvironment();
      const initialConfig =
        ENVIRONMENT_CONFIGS[currentEnv] || ENVIRONMENT_CONFIGS.production;

      return {
        // Estado inicial
        currentEnvironment: currentEnv,
        config: initialConfig,
        metrics: { ...initialMetrics },

        // Actions básicas
        setEnvironment: (env) => {
          const newConfig = ENVIRONMENT_CONFIGS[env];
          if (newConfig) {
            set({
              currentEnvironment: env,
              config: newConfig,
            });
          }
        },

        getConfigForEnvironment: (env) => {
          return ENVIRONMENT_CONFIGS[env] || null;
        },

        // Rotación de claves
        setActiveKid: (kid) => {
          set((state) => {
            if (state.config.keyMap[kid]) {
              return {
                config: {
                  ...state.config,
                  activeKid: kid,
                },
              };
            }
            return state;
          });
        },

        addKeyToMap: (kid, salt) => {
          set((state) => ({
            config: {
              ...state.config,
              keyMap: {
                ...state.config.keyMap,
                [kid]: salt,
              },
            },
          }));
        },

        removeKeyFromMap: (kid) => {
          set((state) => {
            if (kid === state.config.activeKid) {
              // No permitir eliminar el kid activo
              return state;
            }

            const newKeyMap = { ...state.config.keyMap };
            delete newKeyMap[kid];

            return {
              config: {
                ...state.config,
                keyMap: newKeyMap,
              },
            };
          });
        },

        // Métricas
        incrementEncodeAttempts: () => {
          set((state) => ({
            metrics: {
              ...state.metrics,
              encodeAttempts: state.metrics.encodeAttempts + 1,
            },
          }));
        },

        incrementDecodeAttempts: () => {
          set((state) => ({
            metrics: {
              ...state.metrics,
              decodeAttempts: state.metrics.decodeAttempts + 1,
            },
          }));
        },

        incrementDecodeSuccesses: () => {
          set((state) => ({
            metrics: {
              ...state.metrics,
              decodeSuccesses: state.metrics.decodeSuccesses + 1,
            },
          }));
        },

        incrementDecodeFailures: (errorType) => {
          set((state) => {
            const newMetrics = {
              ...state.metrics,
              decodeFailures: state.metrics.decodeFailures + 1,
            };

            // Incrementar contador específico del tipo de error
            switch (errorType) {
              case "CHECKSUM_MISMATCH":
                newMetrics.checksumFailures++;
                break;
              case "INVALID_VERSION":
                newMetrics.versionFailures++;
                break;
              case "INVALID_KID":
                newMetrics.kidFailures++;
                break;
              case "INVALID_LENGTH":
                newMetrics.lengthFailures++;
                break;
              case "INVALID_CHAR":
                newMetrics.charFailures++;
                break;
              default:
                newMetrics.unknownFailures++;
                break;
            }

            return { metrics: newMetrics };
          });
        },

        resetMetrics: () => {
          set({ metrics: { ...initialMetrics } });
        },

        // Computed getters
        getActiveConfig: () => {
          return get().config;
        },

        getMetricsSummary: () => {
          const state = get();
          const { metrics } = state;

          const totalAttempts = metrics.decodeAttempts;
          const successRate =
            totalAttempts > 0 ? metrics.decodeSuccesses / totalAttempts : 0;
          const failureRate =
            totalAttempts > 0 ? metrics.decodeFailures / totalAttempts : 0;

          return {
            totalAttempts,
            successRate,
            failureRate,
            errorBreakdown: {
              checksumFailures: metrics.checksumFailures,
              versionFailures: metrics.versionFailures,
              lengthFailures: metrics.lengthFailures,
              charFailures: metrics.charFailures,
            },
          };
        },
      };
    },
    {
      name: "opaque-id-storage",
      // Solo persistir configuración y métricas, no estado temporal
      partialize: (state) => ({
        currentEnvironment: state.currentEnvironment,
        metrics: state.metrics,
        // No persistir config ya que se deriva del environment
      }),

      // Rehydration: reconstruir config basado en environment persistido
      onRehydrateStorage: () => (state) => {
        if (state) {
          const config =
            ENVIRONMENT_CONFIGS[state.currentEnvironment] ||
            ENVIRONMENT_CONFIGS.production;
          state.config = config;
        }
      },
    }
  )
);

// Selectores útiles
export const useOpaqueIdConfig = () =>
  useOpaqueIdStore((state) => state.config);

export const useOpaqueIdMetrics = () =>
  useOpaqueIdStore((state) => state.getMetricsSummary());

export const useOpaqueIdEnvironment = () =>
  useOpaqueIdStore((state) => state.currentEnvironment);

export const useOpaqueIdActions = () =>
  useOpaqueIdStore((state) => ({
    setEnvironment: state.setEnvironment,
    incrementEncodeAttempts: state.incrementEncodeAttempts,
    incrementDecodeAttempts: state.incrementDecodeAttempts,
    incrementDecodeSuccesses: state.incrementDecodeSuccesses,
    incrementDecodeFailures: state.incrementDecodeFailures,
    resetMetrics: state.resetMetrics,
  }));

// Exportar configuraciones para testing
export { ENVIRONMENT_CONFIGS };
